import { useCallback, useMemo, useState } from 'react'
import { useCurrentRefinements, useHits } from 'react-instantsearch'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { Hit as AlgoliaHit } from '@algolia/client-search'
import { useMediaQuery } from '@react-hookz/web'
import type {
  CellContext,
  ColumnDef,
  SortingState,
} from '@tanstack/react-table'
import { clsx } from 'clsx'
import { observer } from 'mobx-react'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { Table } from 'Components/_theme'

import { useStore } from 'Stores/index'

import { buttonize, getFullContactName } from 'Utilities'

import { PORTAL_BREAKPOINTS } from 'Constants/constants'
import { E2E_TABLE_LINK_EMPLOYEES } from 'Constants/e2e'

import type { SearchResultsDocument } from 'Types/globalSearch/searchResultsDocument.types'

import { composePath } from 'Portal/src/utilities/composePath/composePath.util'

import type {
  EmployeesTableProps,
  EmployeeWithUrl,
} from './EmployeesTable.types'

export const EmployeesTable = observer((props: EmployeesTableProps) => {
  const { isLoading, previewedRowIndex, onOpenPreviewColumn } = props
  const [sorting, setSorting] = useState<SortingState>([])
  const { hits } = useHits<AlgoliaHit<SearchResultsDocument>>()
  const intl = useIntl()
  const { organisationStore } = useStore()
  const hasRentCategories =
    (organisationStore.organisation?.rentCategoryTiers?.rentCategoryTiers
      ?.length ?? 0) > 0

  const { organisationId } = useParams<{
    organisationId: string
  }>()

  const { items } = useCurrentRefinements()

  const navigate = useNavigate()
  const isMobile = useMediaQuery(`(max-width: ${PORTAL_BREAKPOINTS.LG})`)

  const handleEnterDetails = useCallback(
    (
      _event:
        | React.MouseEvent<HTMLDivElement>
        | React.KeyboardEvent<HTMLDivElement>,
      id: string,
      url: string,
      rowIndex: number,
    ) => {
      if (isMobile) {
        navigate(url)

        return
      }

      if (typeof onOpenPreviewColumn === 'function') {
        onOpenPreviewColumn(id, rowIndex)
      }
    },
    [isMobile, navigate, onOpenPreviewColumn],
  )

  const renderEmployeeHeader = useCallback(
    () => (
      <FormattedMessage key="namme" id="EmployeeListContent_header_employee" />
    ),
    [],
  )

  const renderContactHeader = useCallback(
    () => <FormattedMessage id="EmployeeListContent_header_contact" />,
    [],
  )

  const renderRentCategoryTierHeader = useCallback(
    () => <FormattedMessage id="RentCategoryTier" />,
    [],
  )

  const renderDeviceHeader = useCallback(
    () => (
      <FormattedMessage key="name" id="EmployeeListContent_header_device" />
    ),
    [],
  )

  const renderExpandableContent = useCallback(
    ({ cell }: CellContext<EmployeeWithUrl, unknown>) => {
      const employee = cell.row.original

      const rentCategoryTierRow = hasRentCategories && (
        <div className="Table-details-row">
          <span className="Table-row--expandable-title">
            <FormattedMessage id="RentCategoryTier" />
          </span>

          <span>{employee['employee.rent_category_tier'] || '-'}</span>
        </div>
      )

      if (employee['employee.count_active_rentals'] === 0) {
        return (
          <div className="w-100 Table-details">
            {rentCategoryTierRow}

            <div className="Table-details-row">
              <span className="Table-row--expandable-title"> - </span>
            </div>
          </div>
        )
      }

      return (
        <div className="w-100 Table-details">
          {rentCategoryTierRow}

          <div className="Table-details-row">
            <span className="Table-row--expandable-title">
              {employee['employee.count_active_rentals']}
              &nbsp;
              <FormattedMessage id="EmployeeListContent_number_of_devices" />
            </span>
          </div>
        </div>
      )
    },
    [hasRentCategories],
  )

  const renderEmployeeCell = useCallback(
    ({ cell }: CellContext<EmployeeWithUrl, unknown>) => {
      const employee = cell.row.original

      const contact = {
        firstName: employee['employee.first_name'] ?? '',
        lastName: employee['employee.last_name'] ?? '',
      }

      return (
        <div
          className="d-flex flex-column"
          {...buttonize(
            handleEnterDetails,
            employee.id,
            employee.url,
            cell.row.index,
          )}
        >
          <div className="d-flex align-items-center">
            <strong className="text-ellipsis">
              {employee['employee.first_name']
                ? getFullContactName(contact)
                : '-'}
            </strong>
          </div>

          {employee['employee.external_reference'] && (
            <span className="small text-muted text-ellipsis">
              {employee['employee.external_reference']}
            </span>
          )}
        </div>
      )
    },
    [handleEnterDetails],
  )

  const renderContactCell = useCallback(
    ({ cell }: CellContext<EmployeeWithUrl, unknown>) => {
      const employee = cell.row.original

      return (
        <div
          className="d-flex flex-column"
          {...buttonize(
            handleEnterDetails,
            employee.id,
            employee.url,
            cell.row.index,
          )}
        >
          {employee['employee.email'] && (
            <span className="small text-ellipsis">
              {employee['employee.email']}
            </span>
          )}

          {employee['employee.phone_number'] && (
            <span className="text-muted small text-ellipsis">
              {employee['employee.phone_number']}
            </span>
          )}
        </div>
      )
    },
    [handleEnterDetails],
  )

  const renderRentCategoryTierCell = useCallback(
    ({ cell }: CellContext<EmployeeWithUrl, unknown>) => {
      const employee = cell.row.original

      return (
        <div
          className="d-flex flex-column"
          {...buttonize(
            handleEnterDetails,
            employee.id,
            employee.url,
            cell.row.index,
          )}
        >
          <span className="small text-ellipsis">
            {employee['employee.rent_category_tier'] || '-'}
          </span>
        </div>
      )
    },
    [handleEnterDetails],
  )

  const renderDeviceCell = useCallback(
    ({ cell }: CellContext<EmployeeWithUrl, unknown>) => {
      const employee = cell.row.original
      const url = composePath({
        params: {
          employeeId: employee.id,
          organisationId,
        },
        path: GLOBAL_ROUTE_PATHS.organisation.employee,
      })

      if (employee['employee.count_active_rentals'] === 0) {
        return (
          <div
            className="d-flex flex-column"
            {...buttonize(
              handleEnterDetails,
              employee.id,
              employee.url,
              cell.row.index,
            )}
          >
            <span> - </span>
          </div>
        )
      }

      if (
        employee['employee.count_active_rentals'] &&
        employee['employee.count_active_rentals'] > 0
      ) {
        return (
          <div className="d-flex">
            <div className="d-flex align-items-center">
              <Link
                className="d-flex align-items-center h-100 link-gray"
                title={intl.formatMessage({
                  id: 'EmployeeListContent_number_of_devices',
                })}
                to={url}
              >
                <span className="text-ellipsis">
                  {employee['employee.count_active_rentals']}
                  &nbsp;
                  <FormattedMessage id="EmployeeListContent_number_of_devices" />
                </span>
              </Link>
            </div>
            {/* Open previewColumn when click the rest cell space */}
            <div
              className="w-100"
              {...buttonize(
                handleEnterDetails,
                employee.id,
                employee.url,
                cell.row.index,
              )}
              aria-label="Open details"
            />
          </div>
        )
      }

      return null
    },
    [handleEnterDetails, organisationId, intl],
  )

  const columns = useMemo<ColumnDef<EmployeeWithUrl>[]>(() => {
    const cols: ColumnDef<EmployeeWithUrl>[] = [
      {
        accessorFn: (row: EmployeeWithUrl) => row['employee.full_name'],
        cell: renderEmployeeCell,
        enableSorting: true,
        header: renderEmployeeHeader,
        id: 'employee.full_name',
        meta: {
          ExpandableContent: renderExpandableContent,
        },
      },
      {
        cell: renderContactCell,
        header: renderContactHeader,
        id: 'contact',
      },
      ...(hasRentCategories
        ? [
            {
              cell: renderRentCategoryTierCell,
              header: renderRentCategoryTierHeader,
              id: 'rentCategoryTier',
            },
          ]
        : []),
      {
        accessorFn: (row: EmployeeWithUrl) =>
          row['employee.count_active_rentals'],
        cell: renderDeviceCell,
        header: renderDeviceHeader,
        id: 'employee.count_active_rentals',
      },
    ]

    return cols
  }, [
    hasRentCategories,
    renderContactCell,
    renderContactHeader,
    renderDeviceCell,
    renderDeviceHeader,
    renderEmployeeCell,
    renderEmployeeHeader,
    renderExpandableContent,
    renderRentCategoryTierCell,
    renderRentCategoryTierHeader,
  ])

  const data = useMemo<EmployeeWithUrl[]>(
    () =>
      hits.map(hit => {
        const url = composePath({
          params: {
            employeeId: hit['employee.id'],
            organisationId,
          },
          path: GLOBAL_ROUTE_PATHS.organisation.employee,
        })

        return {
          ...hit,
          url,
        }
      }),
    [hits, organisationId],
  )

  const hasFiltersApplied = items.length > 0
  const customNoResultMessage = hasFiltersApplied
    ? intl.formatMessage({
        id: 'Filter_no_results_content',
      })
    : undefined

  return (
    <Table
      className={clsx('Table--employees', 'Table--isAdmin', {
        'Table--employees--with-tiers': hasRentCategories,
      })}
      columns={columns}
      customNoResultsMessage={customNoResultMessage}
      data={data}
      e2eRowId={E2E_TABLE_LINK_EMPLOYEES}
      isLoading={isLoading}
      onSortingChange={setSorting}
      previewedRowIndex={previewedRowIndex}
      sorting={sorting}
    />
  )
})
